/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Splits", description = "Audience Splits unlocks seamless management of A/B and multivariate testing efforts by allowing users to create randomized split groups within audiences in Hightouch.", lastUpdatedDate = new Date(1733356800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Splits",
        description: "Audience Splits unlocks seamless management of A/B and multivariate testing efforts by allowing users to create randomized split groups within audiences in Hightouch.",
        lastUpdatedDate: "Thu Dec 05 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Configure splits",
          "depth": 0,
          "id": "configure-splits"
        }, {
          "value": "Enable holdout groups",
          "depth": 1,
          "id": "enable-holdout-groups"
        }, {
          "value": "Stratified sampling",
          "depth": 1,
          "id": "stratified-sampling"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "How does audience size affect split percentage accuracy?",
          "depth": 1,
          "id": "how-does-audience-size-affect-split-percentage-accuracy"
        }, {
          "value": "How are split groups assigned?",
          "depth": 1,
          "id": "how-are-split-groups-assigned"
        }, {
          "value": "What happens if a user exits and rejoins the same audience?",
          "depth": 1,
          "id": "what-happens-if-a-user-exits-and-rejoins-the-same-audience"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      ul: "ul",
      li: "li",
      img: "img",
      ol: "ol",
      code: "code",
      h3: "h3",
      em: "em"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Audience Splits are part of Customer Studio, available as an add-on to ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier plans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Say you're a marketer and have just defined a new audience of users. You might have questions like, \"Which channel works best here?\" or \"What copy or offer should I use?\""
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ", _jsx(_components.strong, {
          children: "Splits"
        }), " empowers you to use A/B and multivariate testing to answer these questions. With Splits, you can create randomized ", _jsx(_components.strong, {
          children: "split groups"
        }), " or ", _jsx(_components.strong, {
          children: "splits"
        }), " of users within your audience. You can specify:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The number of splits you want to use"
        }), "\n", _jsx(_components.li, {
          children: "The percentage of the total audience membership that each split should have; for example, split A gets 40% and split B gets 60%"
        }), "\n", _jsx(_components.li, {
          children: "The destination to send each split to; for example, split A goes to Facebook, Split B goes to Google, and split C is a holdout that doesn't get sent to any destination"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Using Splits, you can run experiments such as:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Holdout Tests"
          }), ": Send one split to a marketing destination (email, Google, Facebook, etc.) and have the other split act as a control and receive no messaging. These tests use the ", _jsx(_components.a, {
            href: "#enable-holdout-groups",
            children: "holdout group"
          }), " feature and can prove if there was an actual lift from marketing efforts."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Channel Tests"
          }), ": Send splits to different channels to determine the most effective one."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Offer/Creative Tests"
          }), ": Deploy different messaging on each split to see what resonates the best."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "configure-splits",
        children: "Configure splits"
      }), "\n", _jsxs(_components.p, {
        children: ["To configure splits, click into an existing audience on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/audiences",
          children: [_jsx(_components.strong, {
            children: "Audiences"
          }), " overview page"]
        }), ", and select the ", _jsx(_components.strong, {
          children: "Splits"
        }), " tab. From there, you can toggle ", _jsx(_components.strong, {
          children: "Enable splits"
        }), " for the selected audience."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/splits-tab.png",
          alt: "Enabling splits for an audience in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once you've enabled splits for an audience, you need to configure the following:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Split Group Names and Percentages"
          }), ": This is a list of the split group names you want to assign to your audience and the percentage of members to assign to each group."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), ": This is the destination you want to send each split group to. Click ", _jsx(_components.strong, {
            children: "Add sync"
          }), " to add more syncs, and use the dropdown under each split group to assign syncs to split groups."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/splits-config.png",
          alt: "Configuring splits in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Group Column Name (optional)"
          }), ": This is the name of a column that Hightouch automatically creates to denote which split group a row is part of. The default column name is ", _jsx(_components.code, {
            children: "split_column"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Stratification Variables (optional)"
          }), ": This advanced beta feature lets you select columns to use to evenly distribute your splits. For example, you may want an even distribution of \"Age\" across your split groups. For more information, see the ", _jsx(_components.a, {
            href: "#stratified-sampling",
            children: "stratified sampling section"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "enable-holdout-groups",
        children: "Enable holdout groups"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Holdout group"
        }), " acts as a control, and isn't syncable to destinations. Members of the holdout group (and all other split groups) are recorded in your data source so long as audience snapshots are enabled. To enable audience snapshots, navigate to the ", _jsx(_components.strong, {
          children: "Sync log"
        }), " tab on your source's details page."]
      }), "\n", _jsx(_components.p, {
        children: "If you don't want to use the holdout group, you can turn it off by clicking the toggle or setting the distribution to 0%."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/holdout-group.png",
          alt: "Enabling a holdout group in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "stratified-sampling",
        children: "Stratified sampling"
      }), "\n", _jsxs(_components.p, {
        children: ["Stratified sampling lets you define subgroups based on shared characteristics such as demographic traits. Hightouch uses these characteristics to first divide the audience into subgroups or ", _jsx(_components.strong, {
          children: "strata"
        }), " before assigning split groups."]
      }), "\n", _jsx(_components.p, {
        children: "Be aware of the following caveats when using stratified sampling:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Degrading split percentage accuracy:"
          }), " The more stratification variables you define, the less accurate\nthe split percentages may become. This happens because it becomes difficult to represent the appropriate\npercentage of users in each split group as stratification becomes more granular."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Inconsistent assignment:"
          }), " Not all users are guaranteed assignment to the same split group per sync.\nThis removes the ability to perform incremental syncs and forces users to a perform a full resync every\ntime they sync their audience data."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/advanced-configuration.png",
          alt: "Enabling stratified sampling in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "how-does-audience-size-affect-split-percentage-accuracy",
        children: "How does audience size affect split percentage accuracy?"
      }), "\n", _jsxs(_components.p, {
        children: ["A general rule of thumb is ", _jsx(_components.strong, {
          children: "the larger the audience size, the more accurate the split percentages will be."
        }), " This is because there are more people to achieve the desired split percentage for each group."]
      }), "\n", _jsx(_components.p, {
        children: "Other factors that can affect the accuracy of your split percentages include the number of split groups you define and their associated percentages. Generally, the lower the number of groups, the more accurate split percentages can be. The overall audience size and percentages you select for split groups also affect achieved percentage accuracy. For example, it's easier to achieve a perfect 20/40/40 split for an audience whose overall size is a multiple of 20."
      }), "\n", _jsx(_components.h3, {
        id: "how-are-split-groups-assigned",
        children: "How are split groups assigned?"
      }), "\n", _jsxs(_components.p, {
        children: ["Under the hood, Hightouch uses a hashing algorithm that uses the parent model's ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: "primary key"
        }), " to consistently assign split groups."]
      }), "\n", _jsx(_components.h3, {
        id: "what-happens-if-a-user-exits-and-rejoins-the-same-audience",
        children: "What happens if a user exits and rejoins the same audience?"
      }), "\n", _jsx(_components.p, {
        children: "Because of the consistent hashing described above, if a user exits an audience, then rejoins that same audience, they'll automatically get reassigned to the split group they were in before exiting. This assumes that the split definitions haven't changed (for example, changing the percentage sizes of the split groups)."
      }), "\n", _jsx(_components.p, {
        children: "Example:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["User is part of ", _jsx(_components.strong, {
            children: "Split Group A"
          })]
        }), "\n", _jsx(_components.li, {
          children: "User exits the audience"
        }), "\n", _jsx(_components.li, {
          children: "User rejoins the audience"
        }), "\n", _jsxs(_components.li, {
          children: ["User automatically gets reassigned to ", _jsx(_components.strong, {
            children: "Split Group A"
          })]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
